import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { API } from '@/api'
import { message } from '@/components'
import { TeamRole } from '@/consts/team'
import { VOLUME_TYPE } from '@/models/volume'
import {
  createDeleteClickHandler,
  createDomainChangeHandler,
  createFileClickHandler,
  createFileDeleteHandler,
  createMoveClickHandler,
  createRenameClickHandler,
  createRowSelectionHandler,
  createUploadHandler,
  usePathUtilities,
  useUploadingTasks,
} from '@/pages/data-mgr/core/hooks/file-manager-utils'
import { useTeamStore } from '@/stores/team'
import { getEffectiveQuota } from '@/utils/volume'

import { IFile } from './fs'
import * as teamFs from './fs'
import { useTeamDelete } from './useTeamDelete'
import { useTeamMove } from './useTeamMove'
import { useTeamRename } from './useTeamRename'
import { useTeamUpload } from './useTeamUpload'

interface UseTeamFileManagerOptions {
  basePath: string
  userRole: TeamRole
  teamId: number
  teamResID: string
}

interface UseTeamFileManagerReturn {
  // States
  files: IFile[]
  selectedRows: IFile[]
  loading: boolean
  domainInfos: any[]
  currentDomain: number
  currentDomainVolume: { quota_gb: number; usage_mb: number; last_quota_gb: number } | null
  volumes: any[]
  uploadingTasks: Array<{
    name: string
    size: number
    loaded?: number
    status: number
    speed?: number
  }>
  showMgrInstruction: boolean

  // Upload control
  uploadList: any[] // Full upload list for TransferStatusIndicator
  pauseTask: (task: any) => void
  resumeTask: (task: any) => void

  // Path utilities
  dirPath: string
  pathList: string[]
  getPathUrl: (path: string) => string

  // User store derived values
  hasService: boolean
  isOrderExpired: boolean
  notAvailable: boolean

  // Permission computed properties
  canEdit: boolean // Admin or Owner - can modify files
  isReadOnly: boolean // Normal member - read-only access

  // Selection computed properties
  selectedFiles: IFile[] // Selected items that are files (not directories)
  selectedFolders: IFile[] // Selected items that are directories

  // Handlers
  handleRowSelection: (file: IFile, checked: boolean) => void
  handleSelectAll: (checked: boolean) => void
  handleFileClick: (file: IFile) => void
  onFileChange: (event: React.SyntheticEvent) => void
  onRowDeleteClick: (row: IFile) => void
  onRowRenameClick: (row: IFile) => void
  onRowMoveClick: (row: IFile) => void

  // Modal states
  deleteModalOpen: boolean
  setDeleteModalOpen: (open: boolean) => void
  renameInEdit: IFile | undefined
  setRenameInEdit: (file: IFile | undefined) => void
  moveInEdit: IFile[] | undefined
  setMoveInEdit: (files: IFile[] | undefined) => void

  // Actions
  onFileDeleteClick: () => Promise<void>
  fetchFiles: (path?: string, domain?: number) => Promise<boolean>
  onDomainChange: (value: number) => void

  // Translation function
  t: (key: string) => string

  // Team-specific params
  userRole: TeamRole
  teamId: number

  // Dialog components
  RenameDialog: (props: any) => JSX.Element
  MoveDialog: (props: any) => JSX.Element
}

/**
 * Custom hook for managing Team shared datadrive files
 * Similar to useFileManager but uses team FS API with role-based permissions
 */
export function useTeamFileManager(options: UseTeamFileManagerOptions): UseTeamFileManagerReturn {
  const { basePath, userRole, teamId, teamResID } = options
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // States
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [renameInEdit, setRenameInEdit] = useState<IFile | undefined>()
  const [moveInEdit, setMoveInEdit] = useState<IFile[] | undefined>()
  const [selectedRows, setSelectedRows] = useState<IFile[]>([])
  const [files, setFiles] = useState<IFile[]>([])
  const { uploadList, uploadFile, successNum, pauseTask, resumeTask } = useTeamUpload(
    userRole,
    teamId,
  )
  const [currentDomain, setCurrentDomain] = useState(-1)
  const [currentDomainVolume, setCurrentDomainVolume] = useState<{
    quota_gb: number
    usage_mb: number
    last_quota_gb: number
  } | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [volumes, setVolumes] = useState<any[]>([])
  const prevDomainRef = useRef(currentDomain)
  const [loading, setLoading] = useState(false)
  const [domainInfos, setDomainInfos] = useState<any[]>([])
  const [showMgrInstruction, setShowMgrInstruction] = useState(false)

  // Derived values
  const hasService = Boolean(domainInfos.length > 0)
  const notAvailable = currentDomainVolume
    ? getEffectiveQuota(currentDomainVolume as any, isExpired) === 0
    : true
  const isOrderExpired = isExpired

  // Path utilities (using shared hook)
  const { dirPath, pathList, getPathUrl } = usePathUtilities(basePath, searchParams)

  // Filter uploading tasks (using shared hook)
  const uploadingTasks = useUploadingTasks(uploadList)

  // Dialog hooks
  const { RenameDialog } = useTeamRename(currentDomain, userRole, teamId)
  const { MoveDialog } = useTeamMove(currentDomain, userRole, teamId)
  const { deleteFiles } = useTeamDelete(userRole, teamId)

  // Get available domains from team volumes
  const getDomains = useCallback(async () => {
    if (!teamResID) return

    setLoading(true)
    try {
      const [res, domainListRes] = await Promise.all([
        API.getTeamInfo({ res_id: teamResID }),
        API.getDomainList(),
      ])

      if (res.code === 0 && res.team?.volumes) {
        const volumes = res.team.volumes

        // Check expiration from volumes.expireTime
        let expired = false
        if (volumes.expireTime?.seconds) {
          expired = volumes.expireTime.seconds * 1000 < Date.now()
        }
        setIsExpired(expired)

        // Extract and set team services
        if (volumes.services) {
          const domainServices = Object.entries(volumes.services).map(
            ([domainStr, serviceData]) => ({
              domain: parseInt(domainStr, 10),
              services:
                serviceData.services['9']?.accessableServices?.map((s) => ({
                  label: s.label,
                  url: s.value,
                })) || [],
            }),
          )

          useTeamStore.getState().setTeamServices(domainServices)
        }

        // Filter datadrive volumes (type = 1) and exclude domain 3 (Japan)
        if (volumes.subParts) {
          const datadriveVolumes = volumes.subParts.filter(
            (part) => part.type === VOLUME_TYPE.DATA_DRIVE && part.domain !== 3,
          )

          // Extract unique domains
          const uniqueDomains = Array.from(
            new Set(datadriveVolumes.map((vol) => vol.domain)),
          ).sort()

          // Build domainInfos structure with displayName from domain list
          const domainList = domainListRes.code === 0 ? domainListRes.domainInfos || [] : []
          const domainInfos = uniqueDomains.map((domain) => ({
            domain,
            displayName: domainList.find((d: any) => d.domain === domain)?.displayName || '',
          }))

          setDomainInfos(domainInfos)
          setVolumes(datadriveVolumes)

          if (domainInfos.length > 0) {
            const firstDomain = domainInfos[0].domain
            setCurrentDomain(firstDomain)
            setCurrentDomainVolume(
              datadriveVolumes.find((volume: any) => volume.domain === firstDomain) || null,
            )
            setShowMgrInstruction(false)
          } else {
            setShowMgrInstruction(true)
          }
        }
      }
    } catch (error) {
      console.error('[getDomains] Exception:', error)
    } finally {
      setLoading(false)
    }
  }, [teamResID])

  // Fetch files for current directory
  const fetchFiles = useCallback(
    async (p = dirPath, domain = currentDomain) => {
      if (!hasService) {
        return false
      }

      setLoading(true)
      try {
        const { code, msg, files } = await teamFs.ls({
          path: p,
          domain,
          userRole,
          teamId,
        })

        if (code === 0) {
          setSelectedRows([])
          setFiles(files)
          return true
        } else if (code === 15) {
          navigate(basePath)
        } else if (code === -4) {
          if (msg.startsWith('timeout of')) {
            message.error(t('common.errorMsg.fs.timeout'))
          } else if (msg === 'Network Error') {
            message.error(t('common.errorMsg.fs.networkError'))
          }
        } else {
          console.error('[fetchFiles] Error code:', code, 'msg:', msg)
          message.error(t('common.errorMsg.fs.getListFailed'))
        }
        return false
      } catch (error) {
        console.error('[fetchFiles] Exception:', error)
        return false
      } finally {
        setLoading(false)
      }
    },
    [dirPath, currentDomain, hasService, basePath, t, userRole, teamId],
  )

  // Event handlers (using shared utilities)
  const handleRowSelection = createRowSelectionHandler(setSelectedRows)
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedRows(checked ? files : [])
    },
    [files],
  )
  const handleFileClick = createFileClickHandler(
    dirPath,
    selectedRows,
    getPathUrl,
    handleRowSelection,
    navigate,
  )
  const onRowDeleteClick = createDeleteClickHandler(setSelectedRows, setDeleteModalOpen)
  const onRowRenameClick = createRenameClickHandler(setRenameInEdit)
  const onRowMoveClick = createMoveClickHandler(setMoveInEdit)
  const onDomainChange = createDomainChangeHandler(
    volumes,
    setCurrentDomain,
    setCurrentDomainVolume as (volume: { quota_gb: number; usage_mb: number } | null) => void,
  )

  // Upload handler (using shared utility with team params)
  const onFileChange = createUploadHandler(
    currentDomainVolume,
    dirPath,
    currentDomain,
    uploadFile,
    t,
    { userRole, teamId },
  )

  // Delete handler (using shared utility)
  const onFileDeleteClick = createFileDeleteHandler(
    selectedRows,
    dirPath,
    currentDomain,
    deleteFiles,
    fetchFiles,
    setDeleteModalOpen,
    setLoading,
    t,
  )

  // Effect: Handle domain switching
  useEffect(() => {
    if (prevDomainRef.current !== currentDomain) {
      navigate(getPathUrl('/'))
      fetchFiles('/', currentDomain)
      prevDomainRef.current = currentDomain
    } else {
      fetchFiles(dirPath, currentDomain)
    }
  }, [currentDomain, dirPath, successNum, navigate, getPathUrl, fetchFiles])

  // Effect: Get domains on mount
  useEffect(() => {
    getDomains()
  }, [getDomains])

  return {
    // States
    files,
    selectedRows,
    loading,
    domainInfos,
    currentDomain,
    currentDomainVolume,
    volumes,
    uploadingTasks,
    showMgrInstruction,

    // Upload control
    uploadList,
    pauseTask,
    resumeTask,

    // Path utilities
    dirPath,
    pathList,
    getPathUrl,

    // Derived values
    hasService,
    isOrderExpired,
    notAvailable,

    // Permission computed properties
    canEdit: userRole === TeamRole.Admin || userRole === TeamRole.Owner,
    isReadOnly: userRole === TeamRole.Normal,

    // Selection computed properties
    selectedFiles: selectedRows.filter((f) => !f.isDir),
    selectedFolders: selectedRows.filter((f) => f.isDir),

    // Handlers
    handleRowSelection,
    handleSelectAll,
    handleFileClick,
    onFileChange,
    onRowDeleteClick,
    onRowRenameClick,
    onRowMoveClick,

    // Modal states
    deleteModalOpen,
    setDeleteModalOpen,
    renameInEdit,
    setRenameInEdit,
    moveInEdit,
    setMoveInEdit,

    // Actions
    onFileDeleteClick,
    fetchFiles,
    onDomainChange,

    // Translation function
    t,

    // Team-specific params
    userRole,
    teamId,

    // Dialog components
    RenameDialog,
    MoveDialog,
  }
}
